import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";
import { parseRequest } from "../../components/helpers/response_parser";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import StatusMessage from "../../components/ui/StatusMessage";

import { TableShell, Table, Th, Td } from "../../components/data/Table";
import { maskString, maskFullNames } from "../../components/helpers/maskHelpers";
import { playSound } from "../../components/helpers/sound";

const narrowShellStyle = {
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  margin: "0 auto",
  boxSizing: "border-box",
};

const textareaStyle = {
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  minHeight: "180px",
  resize: "vertical",
  boxSizing: "border-box",
  padding: "12px",
  font: "inherit",
  border: "1px solid #d0d5dd",
  borderRadius: "6px",
};

const selectedPayerStyle = {
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  boxSizing: "border-box",
  padding: "12px",
  border: "1px solid #d0d5dd",
  borderRadius: "6px",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

export default function UserBatchCollectOnBus() {
  const { t } = useTranslation();

  // =========================================================
  // BASE DATA
  // =========================================================

  const [vendors, setVendors] = useState([]);
  const [buses, setBuses] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedBusId, setSelectedBusId] = useState("");
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingBuses, setLoadingBuses] = useState(false);
  const [routeMoneyBoxWallets, setRouteMoneyBoxWallets] = useState([]);
  const [forcePayPayers, setForcePayPayers] = useState([]);

  // =========================================================
  // TIME RANGE
  // =========================================================

  const [timeRange, setTimeRange] = useState({ start: "", end: "" });

  // =========================================================
  // NAME INPUT / RESOLUTION
  // =========================================================

  const [namesInput, setNamesInput] = useState("");
  const [nameQueue, setNameQueue] = useState([]);
  const [currentNameIndex, setCurrentNameIndex] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // =========================================================
  // LOGS
  // =========================================================

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // =========================================================
  // COLLECTION
  // =========================================================

  const [collectionList, setCollectionList] = useState([]);
  const [results, setResults] = useState([]);
  const [isCollecting, setIsCollecting] = useState(false);

  // =========================================================
  // MESSAGE
  // =========================================================

  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchVendors();
    fetchRouteMoneyBoxWallets();
    fetchForcePayPayers();
  }, []);

  // =========================================================
  // VENDORS
  // =========================================================

  const fetchVendors = async () => {
    setLoadingVendors(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/vendors`);
      const vendorList = Array.isArray(res.data) ? res.data : [];

      setVendors(vendorList);
      setSelectedVendorId(vendorList.length === 1 ? String(vendorList[0].vendor_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible vendors", err);
      setVendors([]);
      setSelectedVendorId("");
    } finally {
      setLoadingVendors(false);
    }
  };

  // =========================================================
  // BUSES
  // =========================================================

  const fetchBuses = async (vendorId) => {
    if (!vendorId) {
      setBuses([]);
      setSelectedBusId("");
      return;
    }

    setLoadingBuses(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/buses`, {
        params: { vendor_id: vendorId },
      });

      const busList = Array.isArray(res.data) ? res.data : [];

      setBuses(busList);
      setSelectedBusId(busList.length === 1 ? String(busList[0].bus_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible buses", err);
      setBuses([]);
      setSelectedBusId("");
    } finally {
      setLoadingBuses(false);
    }
  };

  // =========================================================
  // WALLETS / PAYERS
  // =========================================================

  const fetchRouteMoneyBoxWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/available-wallets`);
      const wallets = Array.isArray(res.data) ? res.data : [];

      setRouteMoneyBoxWallets(wallets.filter((wallet) => wallet?.is_route_money_box));
    } catch (err) {
      console.error("Failed to fetch route money box wallets", err);
      setRouteMoneyBoxWallets([]);
    }
  };

  const fetchForcePayPayers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/forcepay-payers`);
      setForcePayPayers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch forcepay payers", err);
      setForcePayPayers([]);
    }
  };

  // =========================================================
  // LOGS
  // =========================================================

  const fetchLogs = async () => {
    setLoadingLogs(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/detailed-logs`);
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch detailed logs", err);
      setLogs([]);

      setMessage(err?.response?.data?.detail || t("user_batch_collect_on_bus.messages.logs_failed"));
      setMessageVariant("error");
      playSound("error");
    } finally {
      setLoadingLogs(false);
    }
  };

  // =========================================================
  // RESET RESOLUTION WORKFLOW
  // =========================================================

  const resetResolutionWorkflow = () => {
    setNameQueue([]);
    setCurrentNameIndex(0);
    setSelectedCandidate(null);
    setLogs([]);
    setMessage("");
    setMessageVariant("info");
  };

  // =========================================================
  // VENDOR / BUS SELECTION
  // =========================================================

  useEffect(() => {
    setSelectedBusId("");
    resetResolutionWorkflow();

    if (selectedVendorId) {
      fetchBuses(selectedVendorId);
    } else {
      setBuses([]);
    }
  }, [selectedVendorId]);

  useEffect(() => {
    resetResolutionWorkflow();
  }, [selectedBusId]);

  const handleVendorChange = (e) => {
    setSelectedVendorId(e.target.value);
  };

  const handleBusChange = (e) => {
    setSelectedBusId(e.target.value);
  };

  const vendorDropdownDisabled = loadingVendors || vendors.length <= 1;
  const busDropdownDisabled = loadingBuses || !selectedVendorId || buses.length <= 1;

  // =========================================================
  // FILTER DATA TO SELECTED VENDOR / BUS
  // =========================================================

  const filteredForcePayPayers = useMemo(() => {
    if (!selectedVendorId || !selectedBusId) return [];

    return forcePayPayers.filter((row) => {
      const vendorMatch = row.vendor_id == null || String(row.vendor_id) === String(selectedVendorId);
      const busMatch = row.bus_id == null || String(row.bus_id) === String(selectedBusId);

      return vendorMatch && busMatch;
    });
  }, [forcePayPayers, selectedVendorId, selectedBusId]);

  const filteredRouteMoneyBoxWallets = useMemo(() => {
    if (!selectedVendorId || !selectedBusId) return [];

    return routeMoneyBoxWallets.filter((wallet) => {
      const vendorMatch = wallet.vendor_id == null || String(wallet.vendor_id) === String(selectedVendorId);
      const busMatch = wallet.bus_id == null || String(wallet.bus_id) === String(selectedBusId);

      return vendorMatch && busMatch;
    });
  }, [routeMoneyBoxWallets, selectedVendorId, selectedBusId]);

  const routeMoneyBoxWalletById = useMemo(() => {
    const map = new Map();

    filteredRouteMoneyBoxWallets.forEach((wallet) => {
      map.set(String(wallet.wallet_id), wallet);
    });

    return map;
  }, [filteredRouteMoneyBoxWallets]);

  // =========================================================
  // CURRENT NAME
  // =========================================================

  const currentName = nameQueue[currentNameIndex] || "";
  const hasCurrentName = currentNameIndex < nameQueue.length;
  const allNamesResolved = nameQueue.length > 0 && currentNameIndex >= nameQueue.length;

  // =========================================================
  // CANDIDATE SEARCH
  // =========================================================

  const candidateRows = useMemo(() => {
    if (!currentName) return [];

    const search = currentName.trim().toLocaleLowerCase();
    if (!search) return [];

    return filteredForcePayPayers.filter((row) => {
      const payer = row.payer || {};

      const searchableText = [
        payer.fullname,
        payer.userno,
        payer.sponsor,
        payer.notes,
        row.notes,
        row.walletname,
        row.route_name,
        row.start_point,
        row.end_point,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();

      return searchableText.includes(search);
    });
  }, [currentName, filteredForcePayPayers]);

  // =========================================================
  // SELECTED PAYER LOGS
  // =========================================================

  const selectedCandidateLogs = useMemo(() => {
    if (!selectedCandidate || !timeRange.start || !timeRange.end) return [];

    const payerUserId = selectedCandidate?.payer?.user_id;
    if (payerUserId == null) return [];

    const start = new Date(timeRange.start);
    const end = new Date(timeRange.end);

    return logs.filter((log) => {
      const created = new Date(log.created_at);

      return String(log.from_user_id) === String(payerUserId) && created >= start && created <= end;
    });
  }, [logs, selectedCandidate, timeRange]);

  // =========================================================
  // TIDY UP
  // =========================================================

  const handleTidyUp = async () => {
    setMessage("");
    setMessageVariant("info");

    if (!selectedVendorId || !selectedBusId) {
      setMessage(t("user_batch_collect_on_bus.messages.select_vendor_bus"));
      setMessageVariant("error");
      playSound("error");
      return;
    }

    if (!timeRange.start || !timeRange.end) {
      setMessage(t("user_batch_collect_on_bus.messages.select_time_range"));
      setMessageVariant("error");
      playSound("error");
      return;
    }

    if (new Date(timeRange.start) > new Date(timeRange.end)) {
      setMessage(t("user_batch_collect_on_bus.messages.invalid_time_range"));
      setMessageVariant("error");
      playSound("error");
      return;
    }

    const names = namesInput
      .split(/\r?\n/)
      .map((name) => name.trim())
      .filter(Boolean);

    if (names.length === 0) {
      setMessage(t("user_batch_collect_on_bus.messages.enter_name"));
      setMessageVariant("error");
      playSound("error");
      return;
    }

    setNamesInput(names.join("\n"));
    setNameQueue(names);
    setCurrentNameIndex(0);
    setSelectedCandidate(null);
    setResults([]);

    await fetchLogs();
  };

  // =========================================================
  // SELECT CANDIDATE FOR VERIFICATION
  // =========================================================

  const handleCandidateSelect = (row) => {
    setSelectedCandidate(row);
    setMessage("");
    setMessageVariant("info");
  };

  // =========================================================
  // ADVANCE TO NEXT NAME
  // =========================================================

  const advanceToNextName = () => {
    setSelectedCandidate(null);
    setCurrentNameIndex((current) => current + 1);
    setMessage("");
    setMessageVariant("info");
  };

  // =========================================================
  // IGNORE CURRENT NAME
  // =========================================================

  const handleIgnore = () => {
    advanceToNextName();
  };

  // =========================================================
  // ADD SELECTED CANDIDATE TO COLLECTION
  // =========================================================

  const handleSelectForCollection = () => {
    if (!selectedCandidate) return;

    const selectedRow = selectedCandidate;
    const queueKey = `${selectedRow.payer_wallet_id}:${selectedRow.vendor_wallet_id}`;
    const alreadySelected = collectionList.some((item) => item.queueKey === queueKey);

    if (alreadySelected) {
      setMessage(t("user_batch_collect_on_bus.messages.already_selected"));
      setMessageVariant("error");
      playSound("error");
      return;
    }

    const payer = selectedRow.payer || {};

    setCollectionList((current) => [
      ...current,
      {
        queueKey,
        sourceKeyword: currentName,
        userid: payer.user_id ?? payer.userid ?? payer.id ?? "",
        userno: payer.userno ?? "",
        username: payer.fullname ?? payer.username ?? "",
        sponsor: payer.sponsor ?? "",
        walletRoute: selectedRow.route_name ?? "",
        payer_wallet_id: selectedRow.payer_wallet_id,
        vendor_wallet_id: selectedRow.vendor_wallet_id,
        route_name: selectedRow.route_name,
        start_point: selectedRow.start_point,
        end_point: selectedRow.end_point,
        ticket_balance: selectedRow.ticket_balance,
        payer: selectedRow.payer,
      },
    ]);

    advanceToNextName();
  };

  // =========================================================
  // REMOVE FROM COLLECTION LIST
  // =========================================================

  const handleRemove = (queueKey) => {
    if (isCollecting) return;

    setCollectionList((current) => current.filter((item) => item.queueKey !== queueKey));
  };

  // =========================================================
  // MESSAGE HELPERS
  // =========================================================

  const extractValueFromMessage = (backendMessage, key) => {
    if (!backendMessage) return "";

    const match = backendMessage.match(new RegExp(`${key}=([^\\s|]+)`));
    return match?.[1] ?? "";
  };

  const getRouteLabel = (selectedRow, routeMoneyBoxWallet) => {
    return selectedRow?.route_name || routeMoneyBoxWallet?.route_name || "-";
  };

  const buildSuccessMessage = (resultStatus, selectedRow, routeMoneyBoxWallet, data) => {
    const backendMessage = data?.message || "";
    const payerFullname = selectedRow?.payer?.fullname || selectedRow?.username || "";
    const route = getRouteLabel(selectedRow, routeMoneyBoxWallet);

    const ticketBalanceAfter =
      data?.from_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_after") ??
      "";

    return [
      `${t("user_batch_collect_on_bus.result.result")}: ${t(`user_batch_collect_on_bus.result.values.${resultStatus}`, resultStatus)}`,
      `${t("user_batch_collect_on_bus.result.fullname")}: ${payerFullname}`,
      `${t("user_batch_collect_on_bus.result.route")}: ${route}`,
      `${t("user_batch_collect_on_bus.result.ticket_balance_after")}: ${ticketBalanceAfter}`,
    ].join(" | ");
  };

  const buildCancelledMessage = (selectedRow, routeMoneyBoxWallet, data) => {
    const backendMessage = data?.message || "";
    const payerFullname = selectedRow?.payer?.fullname || selectedRow?.username || "";
    const route = getRouteLabel(selectedRow, routeMoneyBoxWallet);

    const resultStatus =
      data?.verdict ||
      extractValueFromMessage(backendMessage, "result_status") ||
      "cancelled";

    const ticketBalanceBefore =
      data?.from_ticket_balance_before ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_before") ??
      extractValueFromMessage(backendMessage, "ticket_balance_before") ??
      selectedRow?.ticket_balance ??
      "";

    const ticketBalanceAfter =
      data?.from_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_after") ??
      "";

    const reasonKey = backendMessage.includes("Not enough ticket balance") ? "not_enough_ticket_balance" : "cancelled";

    return [
      `${t("user_batch_collect_on_bus.result.result")}: ${t(`user_batch_collect_on_bus.result.values.${resultStatus}`, resultStatus)}`,
      `${t("user_batch_collect_on_bus.result.reason")}: ${t(`user_batch_collect_on_bus.result.reasons.${reasonKey}`)}`,
      `${t("user_batch_collect_on_bus.result.fullname")}: ${payerFullname}`,
      `${t("user_batch_collect_on_bus.result.route")}: ${route}`,
      `${t("user_batch_collect_on_bus.result.ticket_balance_before")}: ${ticketBalanceBefore}`,
      `${t("user_batch_collect_on_bus.result.ticket_balance_after")}: ${ticketBalanceAfter}`,
    ].join(" | ");
  };

  // =========================================================
  // BATCH COLLECT
  // =========================================================

  const handleBatchCollect = async () => {
    if (isCollecting || collectionList.length === 0) return;

    setIsCollecting(true);
    setResults([]);
    setMessage("");
    setMessageVariant("info");

    const itemsToProcess = [...collectionList];

    try {
      for (const item of itemsToProcess) {
        const selectedRouteMoneyBoxWallet = routeMoneyBoxWalletById.get(String(item.vendor_wallet_id));

        try {
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/driver/collect-on-bus`,
            {
              from_wallet_id: Number(item.payer_wallet_id),
              delta_tickets_from_wallet: -1,
              delta_money_from_wallet: 0,
              to_wallet_id: Number(item.vendor_wallet_id),
              delta_tickets_to_wallet: 1,
              delta_money_to_wallet: 0,
              header: "collect-on-bus",
            }
          );

          const verdict = String(data?.verdict || "").toLowerCase();
          const isSuccess = verdict === "approved" || verdict === "success";

          const resultMessage = isSuccess
            ? buildSuccessMessage(verdict, item, selectedRouteMoneyBoxWallet, data)
            : buildCancelledMessage(item, selectedRouteMoneyBoxWallet, data);

          setResults((current) => [
            ...current,
            {
              queueKey: item.queueKey,
              variant: isSuccess ? "success" : "error",
              message: resultMessage,
            },
          ]);

          playSound(isSuccess ? "success" : "error");
        } catch (err) {
          const errData = err?.response?.data || {};

          const errMsg =
            errData?.message ||
            errData?.detail ||
            t("user_batch_collect_on_bus.messages.collect_failed");

          const resultMessage = buildCancelledMessage(
            item,
            selectedRouteMoneyBoxWallet,
            {
              verdict: "cancelled",
              message: errMsg,
            }
          );

          setResults((current) => [
            ...current,
            {
              queueKey: item.queueKey,
              variant: "error",
              message: resultMessage,
            },
          ]);

          playSound("error");
        } finally {
          setCollectionList((current) => current.filter((row) => row.queueKey !== item.queueKey));
        }
      }

      await fetchRouteMoneyBoxWallets();
      await fetchForcePayPayers();
    } finally {
      setIsCollecting(false);
    }
  };

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDateTime = (value) => {
    if (!value) return "-";

    return new Date(value)
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <StandardScreen topNav={<TopNav title={t("user_batch_collect_on_bus.title")} mode="main" />}>
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            {/* ================================================= */}
            {/* CARD 1 - SCOPE                                   */}
            {/* ================================================= */}

            <Card title={t("user_batch_collect_on_bus.title")}>
              <Stack gap="3">

                <FormField label={t("user_batch_collect_on_bus.fields.vendor.label")}>
                  <SelectInput value={selectedVendorId} disabled={vendorDropdownDisabled || isCollecting} onChange={handleVendorChange}>
                    <option value="">
                      {loadingVendors
                        ? t("user_batch_collect_on_bus.fields.vendor.loading")
                        : t("user_batch_collect_on_bus.fields.vendor.placeholder")}
                    </option>

                    {vendors.map((vendor) => (
                      <option key={vendor.vendor_id} value={vendor.vendor_id}>
                        {vendor.vendor_id} | {vendor.vendor_name}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("user_batch_collect_on_bus.fields.bus.label")}>
                  <SelectInput value={selectedBusId} disabled={busDropdownDisabled || isCollecting} onChange={handleBusChange}>
                    <option value="">
                      {loadingBuses
                        ? t("user_batch_collect_on_bus.fields.bus.loading")
                        : t("user_batch_collect_on_bus.fields.bus.placeholder")}
                    </option>

                    {buses.map((bus) => (
                      <option key={bus.bus_id} value={bus.bus_id}>
                        {bus.bus_id} | {bus.plate_number}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField>
                  <TimeRangeSelector onRangeChange={setTimeRange} />
                </FormField>

              </Stack>
            </Card>

            {/* ================================================= */}
            {/* CARD 2 - ENTER NAMES                             */}
            {/* ================================================= */}

            <Card title={t("user_batch_collect_on_bus.fields.names.title")}>
              <Stack gap="3">

                <FormField label={t("user_batch_collect_on_bus.fields.names.label")}>
                  <textarea
                    style={textareaStyle}
                    value={namesInput}
                    onChange={(e) => setNamesInput(e.target.value)}
                    placeholder={t("user_batch_collect_on_bus.fields.names.placeholder")}
                    disabled={isCollecting}
                  />
                </FormField>

                <Button onClick={handleTidyUp} disabled={isCollecting || loadingLogs} fullWidth>
                  {loadingLogs
                    ? t("user_batch_collect_on_bus.status.loading")
                    : t("user_batch_collect_on_bus.actions.tidy_up")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />

              </Stack>
            </Card>

            {/* ================================================= */}
            {/* CARD 3 - CURRENT NAME / CANDIDATES               */}
            {/* ================================================= */}

            {hasCurrentName && !selectedCandidate && (
              <Card title={currentName}>
                <Stack gap="3">

                  <div>
                    {t("user_batch_collect_on_bus.candidate.progress", {
                      current: currentNameIndex + 1,
                      total: nameQueue.length,
                    })}
                  </div>

                  {candidateRows.length > 0 ? (
                    <TableShell maxHeight="65vh">
                      <Table>
                        <thead>
                          <tr>
                            <Th columnIndex={0} sticky wrap wider>{t("user_batch_collect_on_bus.candidate.table.fullname")}</Th>
                            <Th columnIndex={1} sticky wrap wider>{t("user_batch_collect_on_bus.candidate.table.userno")}</Th>
                            <Th>{t("user_batch_collect_on_bus.candidate.table.sponsor")}</Th>
                            <Th>{t("user_batch_collect_on_bus.candidate.table.profile_notes")}</Th>
                            <Th>{t("user_batch_collect_on_bus.candidate.table.wallet_candidates")}</Th>
                            <Th>{t("user_batch_collect_on_bus.candidate.table.action")}</Th>
                          </tr>
                        </thead>

                        <tbody>
                          {candidateRows.map((row, index) => (
                            <tr key={`${row.payer?.user_id}-${row.payer_wallet_id}-${index}`}>
                              <Td columnIndex={0} sticky wrap wider>{maskFullNames(row.payer?.fullname) || "-"}</Td>
                              <Td columnIndex={1} sticky wrap wider>{maskString(row.payer?.userno) || "-"}</Td>
                              <Td>{maskFullNames(row.payer?.sponsor) || "-"}</Td>
                              <Td>{row.payer?.notes || row.notes || "-"}</Td>
                              <Td>
                                {[
                                  row.route_name,
                                  row.start_point && row.end_point ? `${row.start_point} → ${row.end_point}` : null,
                                  row.walletname,
                                  row.payer_wallet_id
                                    ? `${t("user_batch_collect_on_bus.candidate.wallet")} ${row.payer_wallet_id}`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" | ") || "-"}
                              </Td>
                              <Td><Button onClick={() => handleCandidateSelect(row)} disabled={isCollecting}>{t("user_batch_collect_on_bus.actions.select")}</Button></Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableShell>
                  ) : (
                    <StatusMessage variant="info" message={t("user_batch_collect_on_bus.messages.no_candidates", { name: currentName })} />
                  )}

                  <Button onClick={handleIgnore} disabled={isCollecting} fullWidth>{t("user_batch_collect_on_bus.actions.ignore")}</Button>

                </Stack>
              </Card>
            )}

            {/* ================================================= */}
            {/* CARD 3B - SELECTED CANDIDATE + LOGS              */}
            {/* ================================================= */}

            {hasCurrentName && selectedCandidate && (
              <Card title={currentName}>
                <Stack gap="3">

                  <div style={selectedPayerStyle}>
                    <strong>{t("user_batch_collect_on_bus.verification.selected_payer")}:</strong>{" "}
                    {maskFullNames(selectedCandidate?.payer?.fullname) || "-"}
                    {" | "}
                    {maskString(selectedCandidate?.payer?.userno) || "-"}
                    {" | "}
                    {maskFullNames(selectedCandidate?.payer?.sponsor) || "-"}
                    {" | "}
                    {selectedCandidate?.route_name || "-"}
                    {" | "}
                    {selectedCandidate?.start_point && selectedCandidate?.end_point
                      ? `${selectedCandidate.start_point} → ${selectedCandidate.end_point}`
                      : "-"}
                  </div>

                  {selectedCandidateLogs.length > 0 ? (
                    <TableShell maxHeight="65vh">
                      <Table>
                        <thead>
                          <tr>
                            <Th columnIndex={0} sticky>{t("user_batch_collect_on_bus.verification.table.treq_id")}</Th>
                            <Th columnIndex={1} sticky>{t("user_batch_collect_on_bus.verification.table.sender_fullname")}</Th>
                            <Th>{t("user_batch_collect_on_bus.verification.table.recipient_fullname")}</Th>
                            <Th>{t("user_batch_collect_on_bus.verification.table.sender_wallet_id")}</Th>
                            <Th>{t("user_batch_collect_on_bus.verification.table.created_at")}</Th>
                            <Th>{t("user_batch_collect_on_bus.verification.table.request")}</Th>
                            <Th>{t("user_batch_collect_on_bus.verification.table.status")}</Th>
                          </tr>
                        </thead>

                        <tbody>
                          {selectedCandidateLogs.map((log, index) => (
                            <tr key={log.treq_id ?? index}>
                              <Td columnIndex={0} sticky>{log.treq_id}</Td>
                              <Td columnIndex={1} sticky>{log.from_fullname || "-"}</Td>
                              <Td>{log.to_fullname || "-"}</Td>
                              <Td>{log.from_wallet_id ?? "-"}</Td>
                              <Td>{formatDateTime(log.created_at)}</Td>
                              <Td>{parseRequest(log.response)}</Td>
                              <Td>{t(`status_dropdown.values.${log.status}`)}</Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableShell>
                  ) : (
                    <StatusMessage variant="info" message={t("user_batch_collect_on_bus.messages.no_logs")} />
                  )}

                  <Stack gap="2">
                    <Button onClick={handleSelectForCollection} disabled={isCollecting} fullWidth>{t("user_batch_collect_on_bus.actions.select")}</Button>
                    <Button onClick={handleIgnore} disabled={isCollecting || selectedCandidateLogs.length === 0} variant="danger" fullWidth>{t("user_batch_collect_on_bus.actions.ignore")}</Button>
                  </Stack>

                </Stack>
              </Card>
            )}

            {/* ================================================= */}
            {/* ALL NAMES RESOLVED                               */}
            {/* ================================================= */}

            {allNamesResolved && (
              <Card>
                <StatusMessage variant="success" message={t("user_batch_collect_on_bus.messages.all_names_processed")} />
              </Card>
            )}

            {/* ================================================= */}
            {/* COLLECTION LIST                                  */}
            {/* ================================================= */}

            {collectionList.length > 0 && (
              <Card title={t("user_batch_collect_on_bus.collection.title")}>
                <TableShell maxHeight="65vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky wrap wider>{t("user_batch_collect_on_bus.table.username")}</Th>
                        <Th columnIndex={1} sticky wrap wider>{t("user_batch_collect_on_bus.table.userno")}</Th>
                        <Th>{t("user_batch_collect_on_bus.table.userid")}</Th>
                        <Th>{t("user_batch_collect_on_bus.table.sponsor")}</Th>
                        <Th>{t("user_batch_collect_on_bus.table.route")}</Th>
                        <Th>{t("user_batch_collect_on_bus.table.remove")}</Th>
                      </tr>
                    </thead>

                    <tbody>
                      {collectionList.map((item, index) => (
                        <tr key={`${item.queueKey}-${index}`}>
                          <Td columnIndex={0} sticky wrap wider>{maskFullNames(item.username) || "-"}</Td>
                          <Td columnIndex={1} sticky wrap wider>{maskString(item.userno) || "-"}</Td>
                          <Td>{item.userid || "-"}</Td>
                          <Td>{maskFullNames(item.sponsor) || "-"}</Td>
                          <Td>
                            {[
                              item.walletRoute,
                              item.start_point && item.end_point ? `${item.start_point} → ${item.end_point}` : null,
                            ]
                              .filter(Boolean)
                              .join(" | ") || "-"}
                          </Td>
                          <Td><Button onClick={() => handleRemove(item.queueKey)} disabled={isCollecting}>{t("user_batch_collect_on_bus.actions.remove")}</Button></Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}

            {/* ================================================= */}
            {/* BATCH COLLECT                                    */}
            {/* ================================================= */}

            {collectionList.length > 0 && (
              <Card>
                <Button onClick={handleBatchCollect} disabled={collectionList.length === 0 || isCollecting} fullWidth>
                  {isCollecting
                    ? t("user_batch_collect_on_bus.status.collecting")
                    : t("user_batch_collect_on_bus.actions.batch_collect")}
                </Button>
              </Card>
            )}

            {/* ================================================= */}
            {/* RESULTS                                          */}
            {/* ================================================= */}

            {results.length > 0 && (
              <Card title={t("user_batch_collect_on_bus.results.title")}>
                <Stack gap="2">
                  {results.map((result, index) => (
                    <StatusMessage key={`${result.queueKey}-${index}`} variant={result.variant} message={result.message} />
                  ))}
                </Stack>
              </Card>
            )}

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}
